import React from 'react'
import { Box, Typography } from '@mui/material'
import { TouchApp } from '@mui/icons-material'

interface Props {
  message?: string
  icon?: React.ReactNode
  /** If true, hint will remain until the user interacts. When true, by default interaction must be on the hint element itself. */
  persistUntilInteract?: boolean
  /** When true require the user to tap/click the hint box itself to dismiss (default true) */
  dismissOnTargetOnly?: boolean
  /** Minimum time (ms) the hint should remain visible before it can be dismissed (default 1200) */
  minVisibleMs?: number
  onDismiss?: () => void
}

const InteractionHint: React.FC<Props> = ({ message, icon, persistUntilInteract = true, dismissOnTargetOnly = true, minVisibleMs = 0, onDismiss }): JSX.Element | null => {
  const [visible, setVisible] = React.useState(true)
  const [exiting, setExiting] = React.useState(false)
  const mountedAt = React.useRef<number>(Date.now())
  const exitTimer = React.useRef<number | null>(null)
  const animationTime = 220

  const canDismissNow = React.useCallback(() => {
    return Date.now() - mountedAt.current >= Math.max(0, minVisibleMs)
  }, [minVisibleMs])

  const startExit = React.useCallback(() => {
    if (exiting) return
    setExiting(true)
    exitTimer.current = window.setTimeout(() => {
      setVisible(false)
      setExiting(false)
      if (onDismiss != null) { onDismiss() }
    }, animationTime)
  }, [exiting, onDismiss])

  // If dismissOnTargetOnly is false, listen for a global pointerdown to dismiss
  React.useEffect(() => {
    if (!persistUntilInteract || dismissOnTargetOnly) return undefined
    const handler = (): void => {
      if (canDismissNow()) {
        startExit()
      }
    }
    document.addEventListener('pointerdown', handler)
    return () => {
      document.removeEventListener('pointerdown', handler)
    }
  }, [persistUntilInteract, dismissOnTargetOnly, canDismissNow, startExit])

  // auto-hide when not persisting
  React.useEffect(() => {
    if (persistUntilInteract) return undefined
    const t = window.setTimeout(() => { startExit() }, Math.max(0, minVisibleMs))
    return () => { clearTimeout(t) }
  }, [persistUntilInteract, minVisibleMs, startExit])

  React.useEffect(() => () => {
    if (exitTimer.current != null) {
      clearTimeout(exitTimer.current)
      exitTimer.current = null
    }
  }, [])

  if (!visible) return null

  return (
    <Box sx={{ transition: `opacity ${animationTime}ms ease, transform ${animationTime}ms ease`, opacity: exiting ? 0 : 1, transform: exiting ? 'translateY(-6px)' : 'none', pointerEvents: 'none' }}>
      <Box
        role={persistUntilInteract ? 'button' : undefined}
        tabIndex={persistUntilInteract ? 0 : undefined}
        onPointerDown={() => { if (dismissOnTargetOnly && canDismissNow()) startExit() }}
        onKeyDown={(e) => { if (persistUntilInteract && (e.key === 'Enter' || e.key === ' ') && canDismissNow()) { startExit() } }}
        sx={{ pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        {icon ?? <TouchApp sx={{ fontSize: 'calc(0.5dvw + 48px)', color: 'text.secondary' }} />}
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mt: 2, mb: 2, px: 2 }}>
          {message ?? 'Drag the area to move across the screen'}
        </Typography>
      </Box>
    </Box>
  )
}

InteractionHint.displayName = 'InteractionHint'
export default InteractionHint
